// Las cards de "Recrear video" — ADR-2026-10-09-recrear-video-adentro-de-recrear.
//
// UNA fuente para los dos lugares donde se ven: la grilla de Recrear con el interruptor en
// Video (`InterfazV2Plantillas` y `PlantillasBelleza`) y la pestaña Plantillas del modo.
// La de subir tiene la cara de "Recreá tu propia foto" (punteado celeste, degradé y el
// abanico que se abre en hover) con un video en vez de una foto.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import { empresaRecordada, recordarEmpresa } from '../../empresaDeLaSesion';
import {
  RUTA_RECREAR_VIDEO,
  revisarDuracion,
  type EstadoDeRecrearVideo,
} from './recrearVideo';
import { errorDePeso, medirVideoDeArchivo, subirVideoPropio } from './generarRecrearVideo';
import type { PlantillaDeRecrearVideo } from './plantillas';

/** Ilustración de una claqueta con destellos: comunica "tu video → magia". */
const IlustracionVideo = () => (
  <svg width="96" height="96" viewBox="0 0 96 96" fill="none" aria-hidden>
    {/* Claqueta */}
    <rect x="22" y="36" width="44" height="32" rx="6" fill="#E8EDFF" stroke="#C2CDFF" strokeWidth="1.5" />
    <rect x="26" y="40" width="36" height="24" rx="3" fill="white" />
    {/* Tapa de la claqueta */}
    <path d="M22 36 L26 24 L66 24 L66 36 Z" fill="#C2CDFF" stroke="#C2CDFF" strokeWidth="1" strokeLinejoin="round" />
    <line x1="32" y1="24" x2="36" y2="36" stroke="white" strokeWidth="2" />
    <line x1="44" y1="24" x2="48" y2="36" stroke="white" strokeWidth="2" />
    <line x1="56" y1="24" x2="60" y2="36" stroke="white" strokeWidth="2" />
    {/* Play dentro */}
    <path d="M40 48 L40 58 L50 53 Z" fill="#1644EB" opacity="0.25" />
    {/* Varita / rayo */}
    <path d="M68 30 L76 22" stroke="#1644EB" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="78" cy="20" r="3" fill="#1644EB" opacity="0.15" />
    <circle cx="78" cy="20" r="1.5" fill="#1644EB" />
    {/* Destellos */}
    <circle cx="74" cy="14" r="1.5" fill="#60E7F0" />
    <circle cx="82" cy="24" r="1" fill="#60E7F0" />
    <line x1="84" y1="16" x2="88" y2="16" stroke="#60E7F0" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="80" y1="12" x2="80" y2="8" stroke="#60E7F0" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/** La card fija del principio de la grilla en Video: subir un video propio para recrearlo. */
export const CardSubirVideoParaRecrear: React.FC<{
  aspectClass: string;
  subiendo: boolean;
  error: string | null;
  onClick: () => void;
}> = ({ aspectClass, subiendo, error, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={subiendo}
    className={`group w-full mb-3 sm:mb-4 break-inside-avoid rounded-xl relative overflow-hidden flex flex-col items-center justify-center gap-3 px-5 text-center border-2 border-dashed border-[#7B9CFF] bg-gradient-to-b from-white to-[#F1F4FF] transition-[border-color,box-shadow,transform] duration-300 hover:border-brand-primary hover:shadow-[0_12px_32px_-14px_rgba(22,68,235,0.45)] active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary disabled:pointer-events-none ${aspectClass}`}
  >
    <span
      aria-hidden
      className="pointer-events-none absolute inset-x-0 top-0 h-2/3 bg-[radial-gradient(60%_70%_at_50%_0%,rgba(61,92,255,0.08),transparent)] opacity-70 transition-opacity duration-300 group-hover:opacity-100"
    />
    <span className="relative transition-transform duration-300 group-hover:-translate-y-1">
      {subiendo ? (
        <Loader2 className="h-12 w-12 animate-spin text-brand-primary" />
      ) : (
        <IlustracionVideo />
      )}
    </span>
    <span className="relative flex flex-col items-center gap-1">
      <span className="text-[14px] sm:text-[16px] font-semibold leading-tight text-[#16181D]">
        {subiendo ? 'Subiendo…' : 'Tu video, recreado'}
      </span>
      <span className="max-w-[200px] text-[12.5px] sm:text-[13.5px] leading-snug text-[#6B7080] [text-wrap:balance]">
        Elegí un clip y Atlas lo rehace con tu producto.
      </span>
    </span>
    {error && !subiendo && (
      <span className="relative text-[12px] leading-snug text-red-600">{error}</span>
    )}
  </button>
);

/**
 * Una plantilla: el poster quieto y el video en loop al pasar el mouse (en el teléfono no
 * hay hover: queda el poster). Arriba, qué tipo de cambio hace; abajo, el título.
 */
export const CardPlantillaDeVideo: React.FC<{
  plantilla: PlantillaDeRecrearVideo;
  aspectClass?: string;
  elegida?: boolean;
  onClick: () => void;
}> = ({ plantilla, aspectClass = 'aspect-[9/16]', elegida = false, onClick }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => void videoRef.current?.play().catch(() => {})}
      onMouseLeave={() => {
        const v = videoRef.current;
        if (!v) return;
        v.pause();
        v.currentTime = 0;
      }}
      className={`group w-full mb-3 sm:mb-4 break-inside-avoid rounded-xl overflow-hidden bg-neutral-900 relative block text-left active:scale-[0.99] transition-transform ${
        elegida ? 'ring-2 ring-brand-primary ring-offset-2' : ''
      } ${aspectClass}`}
    >
      {/* Sin `poster`: los cuadros de los clips del catálogo no son el primero del video,
          y la card mostraba una cosa y reproducía otra. `#t=0.1` pinta el cuadro real. */}
      <video
        ref={videoRef}
        src={`${plantilla.videoUrl}#t=0.1`}
        muted
        loop
        playsInline
        preload="metadata"
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500 ease-out"
      />
      <span className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
      <span className="absolute inset-x-3 bottom-3 flex flex-col gap-0.5">
        <span className="text-[14px] font-semibold leading-tight text-white">{plantilla.titulo}</span>
        <span className="text-[12px] leading-snug text-white/80">{plantilla.bajada}</span>
      </span>
    </button>
  );
};

/**
 * Subir un video desde Recrear y abrir el modo con él. `inputDeArchivo` hay que
 * renderizarlo una vez: es el `<input type="file">` oculto que abre `abrirSelector`.
 */
export function useSubidaDeVideoParaRecrear() {
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState<string | null>(() => empresaRecordada());
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user || cancelado) return;
      const { data: p } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', auth.user.id)
        .maybeSingle();
      if (cancelado) return;
      const idCompany = (p as { id_company?: string | null } | null)?.id_company ?? null;
      setCompanyId(idCompany);
      recordarEmpresa(idCompany, auth.user.id);
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  const abrirSelector = useCallback(() => {
    if (!subiendo) inputRef.current?.click();
  }, [subiendo]);

  const abrirPlantilla = useCallback(
    (p: PlantillaDeRecrearVideo) => {
      const state: EstadoDeRecrearVideo = { plantillaId: p.id };
      navigate(RUTA_RECREAR_VIDEO, { state });
    },
    [navigate],
  );

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !companyId) return;
    const pesado = errorDePeso(file);
    if (pesado) {
      setError(pesado);
      return;
    }
    setSubiendo(true);
    setError(null);
    try {
      const medida = await medirVideoDeArchivo(file);
      const revision = revisarDuracion(medida.duracion);
      if (revision.accion === 'bloquear') {
        setError(revision.motivo);
        return;
      }
      const videoSubido = await subirVideoPropio(file, companyId, medida);
      const state: EstadoDeRecrearVideo = { videoSubido };
      navigate(RUTA_RECREAR_VIDEO, { state });
    } catch (err) {
      console.error('[CardsDeRecrearVideo] subir video falló', err);
      setError('No se pudo subir el video. Intentá nuevamente.');
    } finally {
      setSubiendo(false);
    }
  };

  const inputDeArchivo = (
    <input ref={inputRef} type="file" accept="video/*" className="hidden" onChange={onFile} />
  );

  return { subiendo, error, abrirSelector, abrirPlantilla, inputDeArchivo };
}
